import { http } from '../lib/api/http';

const NOTIFICATIONS_ENDPOINT = '/notifications';
const DEFAULT_LIMIT = 50;

const ensureId = value => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'object') {
    if (value.id) return value.id;
    if (value.$id) return value.$id;
  }
  return '';
};

const pickField = (record, keys = []) => {
  if (!record || typeof record !== 'object') return undefined;
  for (const key of keys) {
    if (Object.prototype.hasOwnProperty.call(record, key) && record[key] !== undefined && record[key] !== null) {
      return record[key];
    }
  }
  return undefined;
};

const defaultNotification = {
  id: '',
  type: 'general',
  title: '',
  message: '',
  read: false,
  destinationUrl: '',
  reservationId: '',
  companyId: '',
  createdAt: '',
  updatedAt: '',
  data: {},
  expand: {},
};

export const mapNotificationRecord = record => {
  if (!record || typeof record !== 'object') {
    return { ...defaultNotification };
  }

  const reservationSource =
    pickField(record, ['reservation_id', 'reservationId', 'reservation']) ?? record?.expand?.reservation;
  const companySource = pickField(record, ['company_id', 'companyId', 'company']);

  return {
    id: String(record.id || ''),
    type: record.type || 'general',
    title: record.title || '',
    message: record.message || '',
    read: Boolean(record.read),
    destinationUrl: pickField(record, ['destination_url', 'destinationUrl']) || '',
    reservationId: ensureId(reservationSource),
    companyId: ensureId(companySource),
    createdAt: pickField(record, ['created_at', 'created']) || '',
    updatedAt: pickField(record, ['updated_at', 'updated']) || '',
    data: record.data || {},
    expand: record.expand || {},
  };
};

const buildPayload = payload => {
  const body = {};
  if (!payload || typeof payload !== 'object') {
    return body;
  }

  if (payload.type) body.type = String(payload.type);
  if (payload.title) body.title = String(payload.title);
  if (payload.message) body.message = String(payload.message);
  if (payload.destinationUrl) body.destination_url = String(payload.destinationUrl);

  const companyId = ensureId(payload.companyId ?? payload.company);
  if (companyId) body.company_id = companyId;

  const reservationId = ensureId(payload.reservationId ?? payload.reservation);
  if (reservationId) body.reservation_id = reservationId;

  const createdBy = ensureId(payload.createdById ?? payload.createdBy);
  if (createdBy) body.created_by = createdBy;

  if (payload.data) body.data = payload.data;
  if (typeof payload.read === 'boolean') {
    body.read = payload.read;
  } else {
    body.read = false;
  }

  return body;
};

export async function createNotification(payload) {
  const body = buildPayload(payload);
  if (!body.company_id) {
    throw new Error('La notificación requiere una empresa asociada.');
  }
  const { data } = await http.post(NOTIFICATIONS_ENDPOINT, body);
  return mapNotificationRecord(data);
}

export async function listCompanyNotifications({ companyId, limit = DEFAULT_LIMIT } = {}) {
  const params = {};
  if (companyId) {
    params.company_id = companyId;
  }
  if (limit && Number.isFinite(limit)) {
    params.limit = limit;
  }

  const { data } = await http.get(NOTIFICATIONS_ENDPOINT, {
    params: Object.keys(params).length ? params : undefined,
  });

  if (!Array.isArray(data)) {
    return [];
  }

  return data.map(mapNotificationRecord);
}

export async function markNotificationRead(id, read = true) {
  if (!id) return null;
  const payload = typeof read === 'boolean' ? { read } : {};
  const { data } = await http.patch(`${NOTIFICATIONS_ENDPOINT}/${id}`, payload);
  const readState =
    typeof data?.read === 'boolean' ? data.read : typeof read === 'boolean' ? read : true;
  return {
    id,
    read: readState,
    status: data?.status || 'updated',
  };
}

export async function markNotificationsRead(ids = []) {
  if (!Array.isArray(ids) || !ids.length) return [];
  const uniqueIds = Array.from(new Set(ids.filter(Boolean)));
  await Promise.all(
    uniqueIds.map(id =>
      http.patch(`${NOTIFICATIONS_ENDPOINT}/${id}`, { read: true }).catch(() => null)
    )
  );
  return uniqueIds;
}

export async function deleteNotification(id) {
  if (!id) return;
  await http.delete(`${NOTIFICATIONS_ENDPOINT}/${id}`);
}
